import React, { useState, useCallback } from 'react';
import {
  View,
  Text,
  ActivityIndicator,
  FlatList,
  TouchableOpacity,
  Pressable,
} from 'react-native';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import BackArrow from '@assets/svg/BackArrow';
import { ScaledSheet } from '@utils/scale';
import { getWalletDetailedTransactions } from '@services/api';

export default function GroupedTxnsScreen({ route }) {
  const navigation = useNavigation();
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState(null);

  const txnIds = route?.params?.txn_ids || [];

  const fetchDetails = useCallback(async () => {
    if (!Array.isArray(txnIds) || txnIds.length === 0) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const { data } = await getWalletDetailedTransactions(txnIds);
      setData(data);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log('Error fetching grouped transactions', error);
    } finally {
      setIsLoading(false);
    }
  }, [txnIds]);

  useFocusEffect(
    useCallback(() => {
      fetchDetails();
    }, [fetchDetails]),
  );

  const renderItem = ({ item }) => {
    return (
      <Pressable style={({ pressed }) => [styles.card, pressed && styles.cardPressed]}>
        <View style={styles.cardLeft}>
          <Text style={styles.cardTitle}>{item.title}</Text>
          <Text style={styles.cardTimestamp}>{item.timestamp_txt}</Text>
          {Array.isArray(item.subtitle_lines) && item.subtitle_lines.length > 0 && (
            <View style={styles.subtitleContainer}>
              {item.subtitle_lines.map((line, index) => (
                <Text key={index} style={styles.subtitleText}>
                  {line}
                </Text>
              ))}
            </View>
          )}
        </View>
        <View style={styles.cardRight}>
          <Text
            style={[
              styles.cardAmount,
              item.status_color ? { color: item.status_color } : null,
            ]}
          >
            {item.value_txt}
          </Text>
          {item.status_txt ? (
            <Text
              style={[
                styles.statusText,
                item.status_color ? { color: item.status_color } : null,
              ]}
            >
              {item.status_txt}
            </Text>
          ) : null}
        </View>
      </Pressable>
    );
  };

  if (isLoading) {
    return (
      <View style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <BackArrow />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>Transaction details</Text>
        </View>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#10B981" />
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <BackArrow />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>{data?.title || 'Transaction details'}</Text>
      </View>

      <FlatList
        data={data?.transactions || []}
        keyExtractor={(item, index) => item?.id?.toString() || `txn-${index}`}
        renderItem={renderItem}
        contentContainerStyle={styles.listContent}
        ItemSeparatorComponent={() => <View style={styles.listSeparator} />}
        ListEmptyComponent={
          <View style={styles.emptyContainer}>
            <Text style={styles.emptyText}>No transactions found</Text>
          </View>
        }
        showsVerticalScrollIndicator={false}
      />
    </View>
  );
}

const styles = ScaledSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F4F6',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: '#FFFFFF',
  },
  backButton: {
    padding: 8,
    marginRight: 8,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#111827',
    marginLeft: 4,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  listContent: {
    paddingHorizontal: 16,
    paddingVertical: 16,
  },
  listSeparator: {
    height: 8,
  },
  card: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 14,
  },
  cardPressed: {
    opacity: 0.8,
  },
  cardLeft: {
    flex: 1,
    marginRight: 16,
  },
  cardRight: {
    alignItems: 'flex-end',
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 4,
  },
  cardTimestamp: {
    fontSize: 13,
    color: '#6B7280',
  },
  cardAmount: {
    fontSize: 18,
    fontWeight: '700',
    color: '#111827',
  },
  statusText: {
    fontSize: 12,
    fontWeight: '600',
    marginTop: 2,
    color: '#6B7280',
  },
  subtitleContainer: {
    marginTop: 4,
  },
  subtitleText: {
    fontSize: 12,
    color: '#9CA3AF',
    marginTop: 2,
  },
  emptyContainer: {
    paddingVertical: 64,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyText: {
    fontSize: 16,
    color: '#6B7280',
  },
});

